/**
 * The ONE place a typed or proposed act is dispatched, and the sentences it
 * refuses with.
 *
 * Three surfaces reach a pass through language now — the composer's slash
 * verbs, its armed chip, and the chips a reply proposes in the feed
 * (`engine/acts.ts`) — and `promotePress.ts` already learned what happens when
 * each door carries its own copy of the rules: they drift, one review round
 * apart. So the names, the price verbs, the refusals and the press live here
 * once, in `takeCueAction`'s shape, and a chip that greys out and a press that
 * refuses are provably the same string.
 *
 * Every act here is a door that already exists — the node card's own verbs,
 * checks and forms. Nothing in this module is a new way to change the graph,
 * and nothing dispatches without a person's press arriving at `takeAct`.
 */

import type { ModelEntry } from '../core/models'
import { useStore } from '../core/store'
import { OP_HINT, OP_LABEL, type LogicalOpKind } from '../core/types'
import { asUpNextVerb } from '../core/upNext'
import type { ActKind } from '../engine/command'
import { auditorFor, runAudit } from '../engine/reason'
import { routeReady, type RouteReadyView } from './pressName'
import { afterSelection } from './promotePress'
import { scrollToOutcomeForm } from './Grounding'
import { scrollToRewriteEditor } from './NodeCard'
import { pressRefusal, pressVerb, type PressView } from './upNextPress'

/** What a typed act is called on a chip and the primary — the buttons' own
 *  words for the six moves, and the act commands' names for the rest. */
export const ACT_NAME: Record<ActKind, string> = {
  expand: OP_LABEL.expand,
  deepen: OP_LABEL.deepen,
  challenge: OP_LABEL.challenge,
  diverge: OP_LABEL.diverge,
  experiment: OP_LABEL.experiment,
  invert: OP_LABEL.invert,
  verify: 'Verify',
  panel: 'Lenses',
  audit: 'Examine',
  record: 'Record',
  edit: 'Rewrite',
}

/**
 * How a chip's press names itself — the start of its accessible name, before the
 * node's label.
 *
 * A screen reader landed on "Run", twelve times down a long thread, with the verb,
 * the claim and the price each in a different cell of the row and none of them in
 * the button. So the name carries all three: "Run a challenge on “the soliton
 * claim” · est. +$0.30". The two free forms open a form and run nothing, so
 * they say so in their first word.
 */
export const ACT_PRESS: Record<ActKind, string> = {
  expand: 'Run a branch from',
  deepen: 'Run a deepening of',
  challenge: 'Run a challenge on',
  diverge: 'Run a divergence from',
  experiment: 'Run a test design for',
  invert: 'Run an inversion of',
  verify: 'Run a source check on',
  panel: 'Run three lenses on',
  audit: 'Run a cross-examination of',
  record: 'Open the result form for',
  edit: 'Open the rewrite editor for',
}

/** The chip button's one visible word, which its accessible name starts with. */
export const actPressWord = (kind: ActKind): 'Run' | 'Open' =>
  kind === 'record' || kind === 'edit' ? 'Open' : 'Run'

/** The full accessible name: the act, the node's label in quotes, and what the
 *  price cell says — an estimate, or the status word standing where one would. */
export const actPressName = (kind: ActKind, label: string, tail: string | null): string =>
  `${ACT_PRESS[kind]} “${label}”${tail ? ` · ${tail}` : ''}`

/**
 * Whether a chip's pass has a key on the route it would run on, which is what
 * its price cell and its name say before the press: the figure, or "needs a
 * key". The role's (`routeReady`) and never the selection's — a chip read the
 * selection's once, and with reasoning routed to a company whose key is not
 * stored it priced a Challenge that refuses (invariant 4). A cross-examination
 * runs on another company's model, so its route is the auditor the row
 * resolved; the two free forms spend nothing and need no key.
 */
export function actReady(s: RouteReadyView, kind: ActKind, auditor: ModelEntry | null): boolean {
  if (kind === 'audit') return auditor !== null
  const verb = asUpNextVerb(kind)
  return verb === null || routeReady(s, verb)
}

/**
 * The model a chip's cross-examination would run on, or null — for any other
 * act, and where there is no second house. `runAudit`'s own resolution
 * (`auditorFor`: the house that did not write the branch), per claim, so the
 * chip's figure, its name and its readiness read the model its press would run
 * on — the run gate's chip and the composer's armed chip read it the same way. A
 * proposal row resolved it once against the selection's house, so with
 * reasoning routed elsewhere a chip priced the house that wrote the claim.
 */
export function actAuditor(
  s: Parameters<typeof auditorFor>[0],
  kind: ActKind,
  nodeId: string,
): ModelEntry | null {
  return kind === 'audit' ? (auditorFor(s, nodeId)?.entry ?? null) : null
}

/** A string from outside the vocabulary — a stored chip, a model's proposal —
 *  narrowed against the app's own table or refused. The reason `ACT_NAME` can
 *  be the whitelist is that a kind without a name has no chip to be. */
export function asActKind(kind: string): ActKind | null {
  return kind in ACT_NAME ? (kind as ActKind) : null
}

/** Which verb's rates a chip quotes, or null for the two free forms —
 *  `CUE_PRICE_VERB`'s shape, answered by the one arithmetic (`VerbPrice`). */
export const actPriceVerb = (kind: ActKind): 'move' | 'verify' | 'panel' | 'audit' | null =>
  kind === 'record' || kind === 'edit'
    ? null
    : kind === 'verify' || kind === 'panel' || kind === 'audit'
      ? kind
      : 'move'

/** The run gate's variant, over the loop's own move kinds — every one is
 *  priced (a run proposes no free forms) and the closing scan prices as
 *  itself: from the prompt it would send (`closingScanUsd`, which `VerbPrice`
 *  reads for `scan`), the figure the Overview's press prints, never a move's.
 *  Beside `actPriceVerb` so the two chip vocabularies cannot learn different
 *  arithmetic, and shared by the feed's gate row and the pod's Go — wherever
 *  the go can be pressed, the same figure stands in front of it. */
export const gatePriceVerb = (
  k: LogicalOpKind,
): 'move' | 'verify' | 'priorArt' | 'sweep' | 'panel' | 'audit' | 'scan' =>
  k === 'verify' || k === 'priorArt' || k === 'sweep' || k === 'panel' || k === 'audit'
    ? k
    : k === 'bridge'
      ? 'scan'
      : 'move'

/** What a refusal reads: the lock and the Up next line it may wait on, and the
 *  routing that says whether the pass's role has a key — `upNextPress.ts`'s own
 *  view, since a chip's paid press IS that press. */
export type ActView = PressView

/**
 * The lock's sentence for the one act that cannot wait on the line. It names
 * itself, because it prints just above the Up next line in the thread, where a
 * bare "one thought at a time" read as that line's denial (D162). The
 * cross-examination's key, figure and batch are decided at the press, and a
 * deferred one would run on terms its press did not see. The rewrite chip is
 * not here: it opens an editor, which costs nothing, and the editor's one paid
 * press waits on the line itself (`pressRewrite`).
 */
export const ACT_LOCK_REFUSAL = {
  audit: 'One thought at a time — a cross-examination waits for the current pass to finish.',
} as const

/**
 * Why this act cannot run, in the sentence the reader gets — or null when it
 * can. The rules are the node card's, quoted rather than re-derived: the seed
 * takes the six moves and nothing else (its card renders the checks and forms
 * behind `!isRoot`), and the two free forms are never gated on the lock —
 * recording costs nothing, interrupts nothing and outranks everything, and the
 * rewrite editor's paid press waits on the Up next line (`promoteRefusal` keeps
 * the same split for its own presses).
 *
 * A move, a check or the panel no longer refuses on the lock alone: it joins
 * the Up next line behind the pass in flight, as the same press from a key or
 * the card does (D162). It is refused under the lock only where the line itself
 * cannot take it — full, already up next, a run live, no key — and then in the
 * line's own sentence, asked of `pressRefusal`, never a second copy of it.
 */
export function actRefusal(s: ActView, kind: ActKind, nodeId: string): string | null {
  const node = s.session.nodes[nodeId]
  if (!node) return 'That node is no longer on the graph.'
  if (node.parentId === null && !(kind in OP_HINT)) {
    return 'The seed is the question this exploration asks — checks, results and rewrites belong to the claims derived from it.'
  }
  if (kind === 'record' || kind === 'edit') return null
  const verb = asUpNextVerb(kind)
  if (verb) return pressRefusal(s, verb, nodeId)
  if (s.busy) return ACT_LOCK_REFUSAL.audit
  return null
}

/**
 * The press. Returns whether it was taken — run now, or queued up next — so an
 * armed chip can stay armed over a transient refusal (a full line, the lock on
 * the two acts that cannot wait) and stand down over a permanent one.
 *
 * Selection first, `takePromote`'s load-bearing order: a chip in the feed
 * routinely names a node the reader is not standing on, and the rail comes up
 * with it because the nodes — or the form — are about to arrive in the thread.
 * The two free forms poke a frame later inside `afterSelection`, for D42's
 * whole reason: a poke sharing a tick with the selection is swallowed by the
 * very card the selection mounts.
 */
export function takeAct(kind: ActKind, nodeId: string): boolean {
  const s = useStore.getState()
  const refusal = actRefusal(s, kind, nodeId)
  if (refusal) {
    s.toast(refusal, 'warn')
    return false
  }

  s.revealNode(nodeId)
  s.select(nodeId)
  s.setRail(true)
  s.setRightTab('node')

  if (kind === 'record' || kind === 'edit') {
    afterSelection(nodeId, () => {
      if (kind === 'record') {
        useStore.getState().pokeRecord()
        scrollToOutcomeForm(nodeId)
      } else {
        useStore.getState().pokeRewrite()
        scrollToRewriteEditor(nodeId)
      }
    })
    return true
  }

  // The paid presses that can wait go through the one press path, which runs
  // them now or queues them behind the pass in flight — never a second path
  // around the drain's re-judgement and its durable-save wait. The refusal
  // above asked it the same question, synchronously, so this cannot refuse.
  const verb = asUpNextVerb(kind)
  if (verb) return pressVerb(verb, nodeId) !== 'refused'
  void runAudit(nodeId)
  return true
}
